// ─── IndexedDB Key Store ─────────────────────────────────────────────────────
// Persistent storage for cryptographic keys and session states.
// Uses IndexedDB because:
//   1. CryptoKey objects can be stored directly (structured clone)
//   2. Much larger storage than localStorage
//   3. Async API won't block the UI
//
// In production, this would be the device-local key store.
// Key material NEVER leaves the device unencrypted.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  IdentityKeyPair,
  SignedPreKey,
  OneTimePreKey,
  EncryptionSession,
  PreKeyBundle,
} from "./signal-protocol";

// ─── Constants ───────────────────────────────────────────────────────────────

const DB_NAME = "slumbr-messenger-keys";
const DB_VERSION = 1;

/** Object store names */
const STORES = {
  IDENTITY_KEYS: "identityKeys",
  SIGNED_PRE_KEYS: "signedPreKeys",
  ONE_TIME_PRE_KEYS: "oneTimePreKeys",
  SESSIONS: "sessions",
  PRE_KEY_BUNDLES: "preKeyBundles",
} as const;

// ─── Database Connection ─────────────────────────────────────────────────────

let dbPromise: Promise<IDBDatabase> | null = null;

/** Open (or create) the IndexedDB database */
function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      // Identity keys: one per user ID
      if (!db.objectStoreNames.contains(STORES.IDENTITY_KEYS)) {
        db.createObjectStore(STORES.IDENTITY_KEYS, { keyPath: "userId" });
      }

      // Signed pre-keys: keyed by compound "userId:id"
      if (!db.objectStoreNames.contains(STORES.SIGNED_PRE_KEYS)) {
        const store = db.createObjectStore(STORES.SIGNED_PRE_KEYS, {
          keyPath: "key",
        });
        store.createIndex("userId", "userId", { unique: false });
      }

      // One-time pre-keys: keyed by compound "userId:id"
      if (!db.objectStoreNames.contains(STORES.ONE_TIME_PRE_KEYS)) {
        const store = db.createObjectStore(STORES.ONE_TIME_PRE_KEYS, {
          keyPath: "key",
        });
        store.createIndex("userId", "userId", { unique: false });
      }

      // Sessions: keyed by sessionId
      if (!db.objectStoreNames.contains(STORES.SESSIONS)) {
        const store = db.createObjectStore(STORES.SESSIONS, {
          keyPath: "sessionId",
        });
        store.createIndex("localUserId", "localUserId", { unique: false });
        store.createIndex("remoteUserId", "remoteUserId", { unique: false });
        store.createIndex(
          "userPair",
          ["localUserId", "remoteUserId"],
          { unique: false }
        );
      }

      // Pre-key bundles for mock users (DEV_AUTH_BYPASS)
      if (!db.objectStoreNames.contains(STORES.PRE_KEY_BUNDLES)) {
        db.createObjectStore(STORES.PRE_KEY_BUNDLES, { keyPath: "userId" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
}

/** Get a transaction and object store */
async function getStore(
  storeName: string,
  mode: IDBTransactionMode = "readonly"
): Promise<IDBObjectStore> {
  const db = await openDB();
  const tx = db.transaction(storeName, mode);
  return tx.objectStore(storeName);
}

/** Wrap an IDB request in a promise */
function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Wrap an IDB request that returns an array */
function requestAllToPromise<T>(request: IDBRequest<T[]>): Promise<T[]> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result ?? []);
    request.onerror = () => reject(request.error);
  });
}

// ─── Identity Keys ───────────────────────────────────────────────────────────

/** Store identity key pair for a user */
export async function storeIdentityKeyPair(
  userId: string,
  keyPair: IdentityKeyPair
): Promise<void> {
  const store = await getStore(STORES.IDENTITY_KEYS, "readwrite");
  await requestToPromise(store.put({ userId, ...keyPair }));
}

/** Get identity key pair for a user */
export async function getIdentityKeyPair(
  userId: string
): Promise<IdentityKeyPair | null> {
  const store = await getStore(STORES.IDENTITY_KEYS);
  const result = await requestToPromise(store.get(userId));
  if (!result) return null;
  const { userId: _, ...keyPair } = result;
  return keyPair as IdentityKeyPair;
}

/** Delete identity key pair for a user */
export async function deleteIdentityKeyPair(userId: string): Promise<void> {
  const store = await getStore(STORES.IDENTITY_KEYS, "readwrite");
  await requestToPromise(store.delete(userId));
}

// ─── Signed Pre-Keys ─────────────────────────────────────────────────────────

/** Store a signed pre-key */
export async function storeSignedPreKey(
  userId: string,
  signedPreKey: SignedPreKey
): Promise<void> {
  const store = await getStore(STORES.SIGNED_PRE_KEYS, "readwrite");
  const key = `${userId}:${signedPreKey.id}`;
  await requestToPromise(store.put({ key, userId, ...signedPreKey }));
}

/** Get a specific signed pre-key */
export async function getSignedPreKey(
  userId: string,
  id: number
): Promise<SignedPreKey | null> {
  const store = await getStore(STORES.SIGNED_PRE_KEYS);
  const key = `${userId}:${id}`;
  const result = await requestToPromise(store.get(key));
  if (!result) return null;
  const { key: _, userId: __, ...spk } = result;
  return spk as SignedPreKey;
}

/** Get all signed pre-keys for a user */
export async function getSignedPreKeys(
  userId: string
): Promise<SignedPreKey[]> {
  const store = await getStore(STORES.SIGNED_PRE_KEYS);
  const index = store.index("userId");
  const results = await requestAllToPromise(index.getAll(userId));
  return results.map(({ key: _, userId: __, ...spk }) => spk as SignedPreKey);
}

/** Delete a signed pre-key */
export async function deleteSignedPreKey(
  userId: string,
  id: number
): Promise<void> {
  const store = await getStore(STORES.SIGNED_PRE_KEYS, "readwrite");
  const key = `${userId}:${id}`;
  await requestToPromise(store.delete(key));
}

// ─── One-Time Pre-Keys ───────────────────────────────────────────────────────

/** Store a one-time pre-key */
export async function storeOneTimePreKey(
  userId: string,
  preKey: OneTimePreKey
): Promise<void> {
  const store = await getStore(STORES.ONE_TIME_PRE_KEYS, "readwrite");
  const key = `${userId}:${preKey.id}`;
  await requestToPromise(store.put({ key, userId, ...preKey }));
}

/** Store multiple one-time pre-keys at once */
export async function storeOneTimePreKeys(
  userId: string,
  preKeys: OneTimePreKey[]
): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORES.ONE_TIME_PRE_KEYS, "readwrite");
  const store = tx.objectStore(STORES.ONE_TIME_PRE_KEYS);
  for (const pk of preKeys) {
    const key = `${userId}:${pk.id}`;
    store.put({ key, userId, ...pk });
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Get a specific one-time pre-key */
export async function getOneTimePreKey(
  userId: string,
  id: number
): Promise<OneTimePreKey | null> {
  const store = await getStore(STORES.ONE_TIME_PRE_KEYS);
  const key = `${userId}:${id}`;
  const result = await requestToPromise(store.get(key));
  if (!result) return null;
  const { key: _, userId: __, ...otpk } = result;
  return otpk as OneTimePreKey;
}

/** Get all one-time pre-keys for a user */
export async function getOneTimePreKeys(
  userId: string
): Promise<OneTimePreKey[]> {
  const store = await getStore(STORES.ONE_TIME_PRE_KEYS);
  const index = store.index("userId");
  const results = await requestAllToPromise(index.getAll(userId));
  return results.map(
    ({ key: _, userId: __, ...otpk }) => otpk as OneTimePreKey
  );
}

/** Consume (delete) a one-time pre-key after use */
export async function consumeOneTimePreKey(
  userId: string,
  id: number
): Promise<OneTimePreKey | null> {
  const preKey = await getOneTimePreKey(userId, id);
  if (preKey) {
    const store = await getStore(STORES.ONE_TIME_PRE_KEYS, "readwrite");
    const key = `${userId}:${id}`;
    await requestToPromise(store.delete(key));
  }
  return preKey;
}

// ─── Sessions ────────────────────────────────────────────────────────────────

/** Store an encryption session */
export async function storeSession(
  session: EncryptionSession
): Promise<void> {
  const store = await getStore(STORES.SESSIONS, "readwrite");
  await requestToPromise(store.put(session));
}

/** Get a session by ID */
export async function getSession(
  sessionId: string
): Promise<EncryptionSession | null> {
  const store = await getStore(STORES.SESSIONS);
  const result = await requestToPromise(store.get(sessionId));
  return (result as EncryptionSession) ?? null;
}

/** Get session between two users */
export async function getSessionByUsers(
  localUserId: string,
  remoteUserId: string
): Promise<EncryptionSession | null> {
  const store = await getStore(STORES.SESSIONS);
  const index = store.index("userPair");
  const result = await requestToPromise(
    index.get([localUserId, remoteUserId])
  );
  return (result as EncryptionSession) ?? null;
}

/** Get all sessions for a local user */
export async function getSessionsForUser(
  localUserId: string
): Promise<EncryptionSession[]> {
  const store = await getStore(STORES.SESSIONS);
  const index = store.index("localUserId");
  const results = await requestAllToPromise(index.getAll(localUserId));
  return results as EncryptionSession[];
}

/** Delete a session */
export async function deleteSession(sessionId: string): Promise<void> {
  const store = await getStore(STORES.SESSIONS, "readwrite");
  await requestToPromise(store.delete(sessionId));
}

/** Delete all sessions for a user pair */
export async function deleteSessionsByUsers(
  localUserId: string,
  remoteUserId: string
): Promise<void> {
  const sessions = await getSessionsForUser(localUserId);
  const db = await openDB();
  const tx = db.transaction(STORES.SESSIONS, "readwrite");
  const store = tx.objectStore(STORES.SESSIONS);
  for (const session of sessions) {
    if (session.remoteUserId === remoteUserId) {
      store.delete(session.sessionId);
    }
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── Pre-Key Bundles (DEV_AUTH_BYPASS) ───────────────────────────────────────
// In dev mode, we store mock users' pre-key bundles locally
// since there's no server to distribute them.

/** Store a pre-key bundle for a mock user */
export async function storePreKeyBundle(
  bundle: PreKeyBundle
): Promise<void> {
  const store = await getStore(STORES.PRE_KEY_BUNDLES, "readwrite");
  await requestToPromise(store.put(bundle));
}

/** Get a pre-key bundle for a user */
export async function getPreKeyBundle(
  userId: string
): Promise<PreKeyBundle | null> {
  const store = await getStore(STORES.PRE_KEY_BUNDLES);
  const result = await requestToPromise(store.get(userId));
  return (result as PreKeyBundle) ?? null;
}

/** Delete a pre-key bundle */
export async function deletePreKeyBundle(userId: string): Promise<void> {
  const store = await getStore(STORES.PRE_KEY_BUNDLES, "readwrite");
  await requestToPromise(store.delete(userId));
}

// ─── Bulk Operations ─────────────────────────────────────────────────────────

/** Clear all encryption data (nuclear option) */
export async function clearAllKeys(): Promise<void> {
  const db = await openDB();
  const storeNames = Object.values(STORES);
  const tx = db.transaction(storeNames, "readwrite");
  for (const name of storeNames) {
    tx.objectStore(name).clear();
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Check if identity keys exist for a user */
export async function hasIdentityKeys(userId: string): Promise<boolean> {
  const keyPair = await getIdentityKeyPair(userId);
  return keyPair !== null;
}

/** Get count of remaining one-time pre-keys for a user */
export async function getOneTimePreKeyCount(
  userId: string
): Promise<number> {
  const keys = await getOneTimePreKeys(userId);
  return keys.length;
}

/** Close the database connection (for cleanup) */
export function closeDB(): void {
  if (dbPromise) {
    dbPromise.then((db) => db.close()).catch(() => {});
    dbPromise = null;
  }
}
